import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getEvaluationResult, getEvaluationStatus } from '../services/evaluationService';
import EvaluationResult from '../components/evaluation/EvaluationResult';
import EvaluationStatus from '../components/evaluation/EvaluationStatus';
import Button from '../components/common/Button';
import { FiArrowLeft } from 'react-icons/fi';

const EvaluationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [evaluation, setEvaluation] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pollingInterval, setPollingInterval] = useState(null);
  
  // 使用ref来获取最新的status，避免闭包问题
  const statusRef = useRef(status);
  statusRef.current = status;
  
  // 用于调试的计数器
  const pollCountRef = useRef(0);

  // 加载评估状态
  const loadEvaluationStatus = useCallback(async () => {
    console.log(`[EvaluationDetail] Loading status for evaluation ${id}`);
    try {
      const statusData = await getEvaluationStatus(id);
      console.log(`[EvaluationDetail] Status response:`, statusData);

      // 更新状态
      setStatus(statusData);

      // 注意：不在这里停止轮询，而是在useEffect中监听状态变化并停止轮询
      // 这样可以避免状态更新和轮询逻辑的竞争条件
    } catch (err) {
      console.error(`[EvaluationDetail] Failed to load status for evaluation ${id}:`, err);
      setError('加载评估状态失败');

      // 出错时停止轮询
      if (pollingInterval) {
        console.log(`[EvaluationDetail] Stopping polling due to error`);
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
    }
  }, [id]); // 移除pollingInterval依赖，避免循环

  // 加载评估结果
  const loadEvaluationResult = useCallback(async () => {
    console.log(`[EvaluationDetail] Loading result for evaluation ${id}`);
    setLoading(true);

    try {
      const result = await getEvaluationResult(id);
      console.log(`[EvaluationDetail] Result response:`, result);
      setEvaluation(result);
      setLoading(false);
    } catch (err) {
      console.error(`[EvaluationDetail] Failed to load result for evaluation ${id}:`, err);
      setError('加载评估结果失败');
      setLoading(false);
    }
  }, [id]);

  // 初始加载和轮询设置
  useEffect(() => {
    console.log(`[EvaluationDetail] Initializing polling for evaluation ${id}`);
    
    // 立即加载评估状态
    loadEvaluationStatus();

    // 创建轮询器
    const interval = setInterval(() => {
      // 使用ref获取最新的status，避免闭包问题
      const currentStatus = statusRef.current;
      if (!currentStatus || currentStatus.status === 'pending') {
        pollCountRef.current++;
        console.log(`[EvaluationDetail] Polling status for evaluation ${id} (poll #${pollCountRef.current})`);
        loadEvaluationStatus();
      } else {
        console.log(`[EvaluationDetail] Stopping polling - status is ${currentStatus?.status} (total polls: ${pollCountRef.current})`);
      }
    }, 3000); // 每3秒轮询一次

    setPollingInterval(interval);
    console.log(`[EvaluationDetail] Polling started with interval 3000ms`);

    // 清理函数
    return () => {
      console.log(`[EvaluationDetail] Cleaning up polling interval`);
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [id, loadEvaluationStatus]); // 移除status依赖，避免循环

  // 当状态变化时，更新轮询逻辑
  useEffect(() => {
    console.log(`[EvaluationDetail] Status changed to:`, status?.status);
    
    // 如果状态存在且不是等待中，则停止轮询并处理结果
    if (status && status.status !== 'pending') {
      console.log(`[EvaluationDetail] Evaluation finished with status: ${status.status}`);
      
      // 停止轮询
      if (pollingInterval) {
        console.log(`[EvaluationDetail] Clearing polling interval`);
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }

      // 如果评估已完成，加载评估结果
      if (status.status === 'completed') {
        console.log(`[EvaluationDetail] Loading evaluation result`);
        loadEvaluationResult();
      } else if (status.status === 'failed') {
        console.log(`[EvaluationDetail] Evaluation failed, showing error status`);
      }
    }
  }, [status, loadEvaluationResult, pollingInterval]);

  // 页面卸载时清理
  useEffect(() => {
    return () => {
      console.log(`[EvaluationDetail] Component unmounting, cleaning up for evaluation ${id}`);
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [id, pollingInterval]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/evaluations')}
            className="flex items-center gap-1"
          >
            <FiArrowLeft className="h-4 w-4" />
            返回列表
          </Button>
        </div>
      </div>

      {error ? (
        <div className="p-4 rounded-md bg-destructive/10 text-destructive">
          {error}
        </div>
      ) : !status ? (
        // 当状态还未加载时显示加载中
        <div className="flex items-center justify-center flex-grow">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : status.status === 'pending' ? (
        <div className="flex items-center justify-center flex-grow">
          <EvaluationStatus status="pending" />
        </div>
      ) : status.status === 'failed' ? (
        <div className="flex items-center justify-center flex-grow">
          <EvaluationStatus status="failed" />
        </div>
      ) : (
        <div className="flex-grow">
          <EvaluationResult evaluation={evaluation} loading={loading} />
        </div>
      )}
    </div>
  );
};

export default EvaluationDetailPage;
